/** Site history (all saves, from Git) and per-entry versions with "restore this version". */
import { useState } from 'react';
import { deepEqual, isImagePath, type CommitInfo, type Data, type Field } from '@hotpot/core';
import { Layout } from '../components/Layout.js';
import { Dialog, ErrorBox, Loading } from '../components/ui.js';
import { useT } from '../i18n.js';
import { formatDateTime, relativeTime } from '../lib/format.js';
import { Link } from '../router.js';
import { useAsync, useSite, useToast } from '../state.js';
import { routeForPath } from './SiteHome.js';
import { errorText } from './Login.js';

export function SiteHistory() {
  const { t, lang } = useT();
  const site = useSite();
  const id = site.handle.site.id;
  const commits = useAsync(() => site.content.history(undefined, 60), [site.version]);
  const [open, setOpen] = useState<CommitInfo | null>(null);
  return (
    <Layout>
      <div className="title-row">
        <div>
          <h1>{t('historyTitle')}</h1>
          <p className="desc">{t('historyIntro')}</p>
        </div>
      </div>
      {commits.error ? (
        <ErrorBox error={commits.error} onRetry={commits.reload} />
      ) : !commits.data ? (
        <Loading />
      ) : (
        <ul className="history">
          {commits.data.map((c) => {
            const openable = (c.files ?? []).some((f) => site.content.shapeOfPath(f));
            return (
              <li key={c.sha}>
                <span className="msg">
                  {openable ? (
                    <button className="link-btn ink" style={{ textAlign: 'left' }} onClick={() => setOpen(c)}>
                      {c.message}
                    </button>
                  ) : (
                    c.message
                  )}
                </span>
                <span className="by" title={formatDateTime(c.date, lang)}>
                  {c.author.name} · {relativeTime(c.date, t, lang)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {open && <ChangeDialog commit={open} onClose={() => setOpen(null)} />}
    </Layout>
  );
}

/** One change from the site history: what it did to each page, and "restore the version before this change". */
function ChangeDialog({ commit, onClose }: { commit: CommitInfo; onClose: () => void }) {
  const { t, lang } = useT();
  const site = useSite();
  const id = site.handle.site.id;
  const data = useAsync(async () => {
    const storage = site.handle.storage;
    const parent = storage.parentOf ? await storage.parentOf(commit.sha) : null;
    const out = [];
    for (const path of commit.files ?? []) {
      const shape = site.content.shapeOfPath(path);
      if (!shape) continue;
      const [before, after, current] = await Promise.all([
        parent ? storage.read(path, parent) : Promise.resolve(null),
        storage.read(path, commit.sha),
        storage.read(path),
      ]);
      const parse = (f: { text: string } | null) => (f ? site.content.parse(f.text, shape.format, shape.fields) : null);
      const b = parse(before);
      const a = parse(after);
      const title = site.content.titleOf(shape.ref, a ?? b ?? {});
      // Pictures deleted together with an entry that is a folder of its own come back with it.
      const dir = path.slice(0, path.lastIndexOf('/') + 1);
      const images = site.content.entryIsFolderPath(path) && !current ? (commit.files ?? []).filter((p) => p !== path && p.startsWith(dir) && isImagePath(p)) : [];
      out.push({ path, parent, shape, title, images, before: b, after: a, currentText: current?.text ?? null, beforeText: before?.text ?? null });
    }
    return out;
  }, [commit.sha]);

  return (
    <Dialog
      title={commit.message}
      wide
      onClose={onClose}
      buttons={
        <button className="btn" onClick={onClose}>
          {t('close')}
        </button>
      }
    >
      <p className="muted small" style={{ marginTop: -6 }}>
        {commit.author.name} · {formatDateTime(commit.date, lang)}
      </p>
      {data.error ? (
        <ErrorBox error={data.error} />
      ) : !data.data ? (
        <Loading />
      ) : (
        data.data.map((f) => {
          const to = f.currentText !== null ? routeForPath(site.config, id, f.path) : null;
          const changes = f.before && f.after ? diffData(f.shape.fields, f.before, f.after) : [];
          return (
            <div key={f.path} style={{ marginTop: 18 }}>
              <h3 className="section-h" style={{ fontSize: 18 }}>
                {f.shape.label}: {to ? <Link to={to}>{f.title}</Link> : f.title}
              </h3>
              {!f.before ? (
                <p className="muted">{t('changeCreated')}</p>
              ) : !f.after ? (
                <p className="muted">{t('changeDeleted')}</p>
              ) : changes.length === 0 ? (
                <p className="muted">{t('noChanges')}</p>
              ) : (
                <div className="diff">
                  {changes.map((c) => (
                    <div className="d" key={c.key}>
                      <strong style={{ fontWeight: 500 }}>{c.label}</strong>
                      <span className="old">{c.before}</span>
                      <span className="new">{c.after}</span>
                    </div>
                  ))}
                </div>
              )}
              {f.before && f.parent && <RestoreBefore path={f.path} images={f.images} parent={f.parent} title={f.title} deleted={f.currentText === null} same={f.currentText === f.beforeText} onDone={onClose} />}
            </div>
          );
        })
      )}
    </Dialog>
  );
}

function RestoreBefore({ path, images, parent, title, deleted, same, onDone }: { path: string; images: string[]; parent: string; title: string; deleted: boolean; same: boolean; onDone: () => void }) {
  const { t } = useT();
  const site = useSite();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  if (same) return <p className="hint">{t('alreadyBefore')}</p>;
  return (
    <p style={{ marginTop: 10 }}>
      <button
        className="outline-btn"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try {
            const commit = deleted
              ? await site.content.restoreDeleted(path, images, parent, site.author, title)
              : await site.content.restore(path, parent, site.author, title);
            site.afterCommit(commit);
            toast(t('restored'));
            onDone();
          } catch (e) {
            toast(errorText(e, t));
            setBusy(false);
          }
        }}
      >
        {deleted ? t('restoreDeleted') : t('restoreBefore')}
      </button>
    </p>
  );
}

interface Change {
  key: string;
  label: string;
  before: string;
  after: string;
}

function show(v: unknown): string {
  if (v === undefined || v === null || v === '') return '—';
  if (typeof v === 'string') return v.length > 400 ? v.slice(0, 400) + '…' : v;
  if (Array.isArray(v) && v.every((x) => typeof x !== 'object')) return v.join(', ');
  const s = JSON.stringify(v);
  return s.length > 400 ? s.slice(0, 400) + '…' : s;
}

/** Field-level differences between two versions (objects are compared one level down). */
export function diffData(fields: Field[], before: Data, after: Data): Change[] {
  const out: Change[] = [];
  const labels = new Map<string, string>();
  for (const f of fields) {
    labels.set(f.name, f.label);
    for (const [l, key] of Object.entries(f.translations ?? {})) labels.set(key, `${f.label} (${l.toUpperCase()})`);
  }
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])];
  for (const key of keys) {
    if (deepEqual(before[key], after[key])) continue;
    const field = fields.find((f) => f.name === key);
    const b = before[key];
    const a = after[key];
    if (field?.type === 'object' && b && a && typeof b === 'object' && typeof a === 'object') {
      for (const sub of diffData(field.fields, b as Data, a as Data)) out.push({ ...sub, key: `${key}.${sub.key}`, label: `${field.label} › ${sub.label}` });
      continue;
    }
    out.push({ key, label: labels.get(key) ?? (key === 'body' ? 'Text' : key), before: show(b), after: show(a) });
  }
  return out;
}

export function EntryHistory({ path, fields, format, current, label, onClose, onRestored }: { path: string; fields: Field[]; format: string; current: Data; label: string; onClose: () => void; onRestored: () => void }) {
  const { t, lang } = useT();
  const site = useSite();
  const toast = useToast();
  const commits = useAsync(() => site.content.history(path, 30), [path, site.version]);
  const [selected, setSelected] = useState<CommitInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const version = useAsync(async () => {
    if (!selected) return null;
    const file = await site.handle.storage.read(path, selected.sha);
    if (!file) return { data: null as Data | null };
    return { data: site.content.parse(file.text, format, fields) };
  }, [selected?.sha]);
  const isCurrent = selected && commits.data?.[0]?.sha === selected.sha;
  const changes = version.data?.data ? diffData(fields, version.data.data, current) : [];

  const restore = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const commit = await site.content.restore(path, selected.sha, site.author, label);
      site.afterCommit(commit);
      toast(t('restored'));
      onRestored();
    } catch (e) {
      toast(errorText(e, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={t('historyEntry')}
      wide
      onClose={onClose}
      buttons={
        <>
          <button className="link-btn" onClick={onClose}>
            {t('close')}
          </button>
          {selected && !isCurrent && (
            <button className="btn" onClick={restore} disabled={busy || !version.data?.data}>
              {t('restore')}
            </button>
          )}
        </>
      }
    >
      {commits.error ? (
        <ErrorBox error={commits.error} />
      ) : !commits.data ? (
        <Loading />
      ) : (
        <ul className="history">
          {commits.data.map((c, i) => (
            <li key={c.sha} style={selected?.sha === c.sha ? { background: 'var(--bg)', marginInline: -12, paddingInline: 12, borderRadius: 10 } : undefined}>
              <button className="link-btn ink msg" style={{ textAlign: 'left' }} onClick={() => setSelected(c)} aria-pressed={selected?.sha === c.sha}>
                {c.message}
                {i === 0 && <span className="badge" style={{ marginLeft: 10 }}>{t('current')}</span>}
              </button>
              <span className="by">
                {c.author.name} · {formatDateTime(c.date, lang)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {selected && (
        <div style={{ marginTop: 20 }}>
          <h3 className="section-h" style={{ fontSize: 18 }}>
            {t('changes')}: {formatDateTime(selected.date, lang)} → {t('current').toLowerCase()}
          </h3>
          {version.loading ? (
            <Loading />
          ) : !version.data?.data ? (
            <p className="muted">{t('initialVersion')}</p>
          ) : changes.length === 0 ? (
            <p className="muted">{t('noChanges')}</p>
          ) : (
            <div className="diff">
              {changes.map((c) => (
                <div className="d" key={c.key}>
                  <strong style={{ fontWeight: 500 }}>{c.label}</strong>
                  <span className="old">{c.before}</span>
                  <span className="new">{c.after}</span>
                </div>
              ))}
            </div>
          )}
          {!isCurrent && version.data?.data && <p className="hint">{t('restoreConfirm', { date: formatDateTime(selected.date, lang) })}</p>}
        </div>
      )}
    </Dialog>
  );
}
